import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { RootState } from '../../../store/store';
import {
  ATTENDANCE_LABELS,
  endSessionMilestone,
  EVIDENCE_REASON_LABELS,
  EvidenceFailureReason,
  EvidenceView,
  FINAL_PROJECT_RUBRIC_CRITERIA,
  formatPresence,
  getEvidence,
  MilestoneEvidence,
  RECORDING_STATUS_LABELS,
  Rubric,
  RubricScore,
  SESSION_RUBRIC_CRITERIA,
  submitDeliverable,
  submitNotes,
  submitRubric,
} from '../api/session-evidence';
import {
  ClosingActivity,
  correctRubric,
  FeedbackAudience,
  getTeacherLearningRecords,
  LearningFeedback,
  LearningGateFact,
  LearningObservation,
  newRequestKey,
  ObservationState,
  publishFeedback,
  recordClosingActivity,
  recordObservation,
  recordSuggestedPractice,
  SuggestedPractice,
  TeacherLearningRecords,
} from '../api/learning';
import { completeMilestone, reportNoShow } from '../api/escrow';
import { AbsenceFollowUpPanel, GuardianFollowUpNote } from '../components/AbsenceFollowUp';
import { formatMoney } from '../../../commodity/format/money';
import {
  EVIDENCE_STATUS_LABEL,
  FEEDBACK_AUDIENCE_HELP,
  FEEDBACK_AUDIENCE_LABEL,
  LEARNING_PROFILE_LABEL,
  MILESTONE_STATUS_LABEL,
  OBSERVATION_HELP,
  OBSERVATION_LABEL,
  OBSERVATION_ORDER,
  profileAdmitsRubric,
  profileHasMilestones,
  profileOf,
  SESSION_STATE_LABEL,
  WORKING_VOCABULARY_NOTE,
} from '../vocabulary/learningVocabulary';

/**
 * Teacher evidence workspace for one milestone — ADR-007 Epic 8 (TCH-14),
 * profile-aware since UC-T10 / LEM-01.
 *
 * What the teacher may record depends on the course's learning profile, and
 * the screen shows exactly that and nothing else (MC-01 v1-7, MC-07):
 *  - LEP_0 (Experiencia, T10 not required): nothing assessable — no rubric,
 *    observation or comment form; the family sees the history of classes.
 *  - LEP_1 (Práctica guiada, the LIGHTWEIGHT branch): a qualitative
 *    observation in a working vocabulary, and feedback split by audience. No
 *    rubric, grade or final project exists.
 *  - A course with no profile yet (legacy): the optional rubric and
 *    deliverable it was sold with. Optional — they never hold a payment.
 *  - LEP_2 / LEP_3 are deferred and only appear with an approval.
 *
 * Milestone 8 is the close of the cycle in every profile: it closes when the
 * sessions are delivered. An in-class closing activity may be described there;
 * it is never required (MC01-R04).
 *
 * A record already submitted is never edited in place: changing it is a
 * correction, with a reason, that creates a new version. A correction never
 * moves money, so it stays available after the payment is released.
 *
 * ADR-005 will not release a session's escrow without a recording, a teacher
 * check-in and the minimum duration — and, since MC-01 v1-7, nothing else: no
 * profile waits on a rubric (MC01-R05, R07). The checklist states what is
 * still missing, and the release action only becomes available once nothing is.
 */

const fmtDateTime = (iso?: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('es-MX', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
};

/** STU-11-FR-05 / MC-04 §9 — said where the observation and family comments would be. */
const ABSENT_LEARNER_NOTE =
  'La videollamada registró que el alumno no asistió a esta sesión, así que no se registra una ' +
  'observación ni comentarios para el alumno o su familia. Si quieres escribir a la familia, usa ' +
  'el «Seguimiento al alumno ausente».';

/** The newest version of a record in a list of versions. */
const latestOf = <T extends { version: number }>(rows: T[]): T | undefined =>
  rows.reduce<T | undefined>((a, r) => (!a || r.version > a.version ? r : a), undefined);

const SessionEvidence: React.FC = () => {
  const { enrollmentId, sequence: sequenceParam } = useParams<{
    enrollmentId: string;
    sequence: string;
  }>();
  const navigate = useNavigate();
  const role = useSelector((state: RootState) => (state.auth as any).user?.role as string);
  const isTeacher = role === 'TEACHER';

  const sequence = Number(sequenceParam);
  const [view, setView] = useState<EvidenceView | null>(null);
  const [records, setRecords] = useState<TeacherLearningRecords | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!enrollmentId || !Number.isFinite(sequence)) return;
    setLoading(true);
    setError(null);
    try {
      const [evidence, learning] = await Promise.all([
        getEvidence(enrollmentId, sequence),
        // The teacher's own records; a family surface never asks for them.
        isTeacher
          ? getTeacherLearningRecords(enrollmentId).catch(() => null)
          : Promise.resolve(null),
      ]);
      setView(evidence);
      setRecords(learning ?? null);
    } catch (e: any) {
      setError(e?.message || 'No pudimos cargar la evidencia de esta sesión.');
    } finally {
      setLoading(false);
    }
  }, [enrollmentId, sequence, isTeacher]);

  useEffect(() => {
    void load();
  }, [load]);

  const milestone = view?.milestone;
  const profile = profileOf(view?.learningProfile ?? records?.learningProfile);
  // Whether a rubric may be written at all (never on LEP_0/1). Optional
  // wherever it exists: it holds no payment.
  const withRubric = profileAdmitsRubric(profile);
  // Whether the family is shown formal results: only an approved LEP_2/3.
  const familySeesRubric = profileHasMilestones(profile);
  const isFinalProject = milestone?.type === 'final_project';
  const reasons = view?.validation.reasons ?? [];
  const complete = !!view?.validation.valid;
  const released = milestone?.status === 'released';
  const disputed = milestone?.status === 'disputed';
  const locked = released || disputed;

  const criteria = useMemo(
    () => (isFinalProject ? FINAL_PROJECT_RUBRIC_CRITERIA : SESSION_RUBRIC_CRITERIA),
    [isFinalProject],
  );

  const observation = latestOf(
    (records?.observations ?? []).filter((o) => o.sequence === sequence),
  );
  const feedbackFor = (audience: FeedbackAudience): LearningFeedback | undefined =>
    latestOf(
      (records?.feedback ?? []).filter((f) => f.sequence === sequence && f.audience === audience),
    );
  const practice = latestOf(
    (records?.suggestedPractice ?? []).filter((p) => p.sequence === sequence),
  );
  // STU-11-FR-05 / MC-04 §9: the video call recorded the learner as absent, so
  // nothing the learner or the family reads is written about this session.
  const learnerAbsent = milestone?.attendance === 'NO_SHOW';

  /** Runs a write, folds the fresh evidence back in, and reports the outcome. */
  const run = async (action: () => Promise<unknown>, successMessage: string) => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await action();
      await load();
      setInfo(successMessage);
    } catch (e: any) {
      setError(e?.message || 'No pudimos guardar los cambios.');
    } finally {
      setBusy(false);
    }
  };

  const handleRelease = async () => {
    if (!enrollmentId) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const result = await completeMilestone(enrollmentId, sequence);
      if (result?.release?.released) {
        setInfo(
          `Evidencia completa. Se liberaron ${formatMoney(
            result.release.amount,
            view?.currency,
          )} a tu cuenta.`,
        );
      } else if (isFinalProject) {
        setInfo(
          'Ciclo cerrado. La retención se libera automáticamente tras el periodo de revisión.',
        );
      } else {
        setInfo('Sesión marcada como completa.');
      }
      await load();
    } catch (e: any) {
      setError(e?.message || 'No pudimos completar esta sesión.');
    } finally {
      setBusy(false);
    }
  };

  const handleNoShow = async (party: 'seller' | 'buyer') => {
    if (!enrollmentId) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await reportNoShow(enrollmentId, sequence, party);
      setInfo(
        party === 'buyer'
          ? 'Registramos la inasistencia del alumno.'
          : 'Recibimos tu reporte. Revisamos la sesión y te avisamos del resultado.',
      );
      await load();
    } catch (e: any) {
      setError(e?.message || 'No pudimos registrar la inasistencia.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p>Cargando evidencia…</p>;
  if (error && !view) return <p style={{ color: '#c92a2a' }}>{error}</p>;
  if (!view || !milestone) return <p>No encontramos esta sesión.</p>;

  // Milestone 8 is the close of the cycle in every profile (MC01-R04).
  const title = isFinalProject ? 'Cierre del ciclo' : `Sesión ${milestone.sequence}`;
  const closingActivity = latestOf(records?.closingActivity ?? []);
  const closingOpen = milestone.status === 'pending' || milestone.status === 'in_progress';
  const rubricOnMilestone = isFinalProject ? milestone.finalRubric : milestone.rubric;

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <button onClick={() => navigate(`/enrollments/${enrollmentId}`)} style={backLinkStyle}>
        &larr; Volver a la inscripción
      </button>

      <header style={{ marginBottom: 20 }}>
        <h1 style={{ marginBottom: 4 }}>{title}</h1>
        <p style={{ color: '#666', margin: 0 }}>
          {formatMoney(milestone.amount, view?.currency)} en garantía · estado:{' '}
          <strong>{MILESTONE_STATUS_LABEL[milestone.status] ?? milestone.status}</strong> ·{' '}
          {LEARNING_PROFILE_LABEL[profile]}
        </p>
      </header>

      {error && (
        <div role="alert" style={alertStyle('error')}>
          {error}
        </div>
      )}
      {info && (
        <div role="status" style={alertStyle('success')}>
          {info}
        </div>
      )}

      <EvidenceChecklist
        reasons={reasons}
        complete={complete}
        released={released}
        isFinalProject={isFinalProject}
      />

      {view.gateFact && <GateFactLine fact={view.gateFact} />}

      {!isTeacher && (
        <p style={{ color: '#666', fontSize: 14 }}>
          Esta es la evidencia que el profesor registró para {title.toLowerCase()}. Sólo el profesor
          puede modificarla.
        </p>
      )}

      {!isFinalProject && (
        <SessionFacts
          milestone={milestone}
          sessionState={view.sessionState}
          isTeacher={isTeacher}
        />
      )}

      {isFinalProject && (
        <Section title="Cierre del ciclo">
          <p style={mutedStyle}>
            El ciclo se cierra solo cuando todas las sesiones se impartieron con su evidencia de
            entrega. No hace falta proyecto final, entrega ni calificación.
          </p>
        </Section>
      )}

      {isTeacher && isFinalProject && records?.admits.closingActivity !== false && (
        <ClosingActivityForm
          busy={busy}
          current={closingActivity}
          onSubmit={(body) =>
            run(
              () => recordClosingActivity(enrollmentId!, body),
              body.expectedVersion
                ? 'Guardamos la corrección de la actividad de cierre.'
                : 'Guardamos la actividad de cierre.',
            )
          }
        />
      )}

      {isTeacher && !locked && !isFinalProject && (
        <>
          <Section title="Asistencia">
            <p style={mutedStyle}>
              La videollamada registra tu asistencia y la del alumno cuando entran y salen; nadie
              puede marcarla a mano.
              {milestone.teacherCheckedInAt
                ? ' Tu entrada ya está registrada.'
                : ' Tu entrada todavía no está registrada.'}
            </p>
            {milestone.sessionOutcome === 'INTERRUPTED' && (
              <p role="status" style={mutedStyle}>
                La videollamada terminó sin que cerraras la clase. Guardamos lo que quedó registrado
                y el pago queda en revisión.
              </p>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {milestone.sessionStartedAt && !milestone.sessionEndedAt && (
                <button
                  disabled={busy}
                  onClick={() =>
                    run(
                      () => endSessionMilestone(enrollmentId!, sequence),
                      'Cerramos la sesión y registramos su duración.',
                    )
                  }
                  style={btn(busy, '#c92a2a')}
                >
                  Cerrar sesión
                </button>
              )}
            </div>
          </Section>

          <Section title="El alumno no se presentó">
            <p style={mutedStyle}>
              Si el alumno no llegó, puedes reportarlo cuando pase el tiempo de espera de la clase.
              El reporte no libera el pago por sí solo: la clase se liquida si la evidencia de
              entrega está completa.
            </p>
            <button
              disabled={busy}
              onClick={() => handleNoShow('buyer')}
              style={btn(busy, '#c92a2a')}
            >
              Reportar inasistencia del alumno
            </button>
          </Section>
        </>
      )}

      {/* MC-04 — the optional, approved follow-up to the guardian of a learner
          the video call recorded as absent. Not gated on payment state: it
          neither depends on nor changes it. */}
      {isTeacher && !isFinalProject && milestone.attendance === 'NO_SHOW' && (
        <AbsenceFollowUpPanel enrollmentId={enrollmentId!} sequence={sequence} />
      )}
      {!isTeacher && !isFinalProject && (
        <GuardianFollowUpNote enrollmentId={enrollmentId!} sequence={sequence} />
      )}

      {isTeacher && !locked && isFinalProject && withRubric && (
        <DeliverableForm
          busy={busy}
          current={milestone.deliverableUri}
          onSubmit={(uri) =>
            run(
              () => submitDeliverable(enrollmentId!, sequence, uri),
              'Guardamos el entregable del proyecto final.',
            )
          }
        />
      )}

      {/* ── What this profile lets the teacher record ────────────────── */}
      {isTeacher && !disputed && withRubric && (
        <RubricBlock
          busy={busy}
          criteria={criteria}
          existing={rubricOnMilestone}
          asFinalRubric={isFinalProject}
          onSubmit={(payload) =>
            run(
              () => submitRubric(enrollmentId!, sequence, { ...payload }),
              'Guardamos la evaluación.',
            )
          }
          onCorrect={(payload) =>
            run(
              () => correctRubric(enrollmentId!, sequence, payload),
              'Guardamos la corrección como una versión nueva. El pago no cambia.',
            )
          }
        />
      )}

      {isTeacher && !disputed && profile === 'LEP_1' && !isFinalProject && learnerAbsent && (
        <Section title="Observación de la sesión">
          <p style={mutedStyle}>{ABSENT_LEARNER_NOTE}</p>
        </Section>
      )}

      {isTeacher && !disputed && profile === 'LEP_1' && !isFinalProject && !learnerAbsent && (
        <ObservationForm
          busy={busy}
          sessionStarted={!!milestone.sessionStartedAt}
          current={observation}
          onSubmit={(body) =>
            run(
              () => recordObservation(enrollmentId!, sequence, body),
              body.expectedVersion
                ? 'Guardamos la corrección de la observación.'
                : 'Guardamos la observación de la sesión.',
            )
          }
        />
      )}

      {isTeacher &&
        !disputed &&
        records?.admits.feedback !== false &&
        profile !== 'LEP_0' &&
        !(isFinalProject && !withRubric) && (
          <Section title="Comentarios de la sesión">
            {!milestone.sessionStartedAt && !isFinalProject ? (
              <p style={mutedStyle}>Podrás dejar comentarios cuando la sesión haya empezado.</p>
            ) : (
              <>
                {learnerAbsent && !isFinalProject && (
                  <p style={mutedStyle}>{ABSENT_LEARNER_NOTE} Tu nota privada sigue disponible.</p>
                )}
                {(learnerAbsent && !isFinalProject
                  ? (['TEACHER_PRIVATE'] as FeedbackAudience[])
                  : (['LEARNER_SHARED', 'PARENT_PRIVATE', 'TEACHER_PRIVATE'] as FeedbackAudience[])
                ).map((audience) => (
                  <FeedbackForm
                    key={audience}
                    audience={audience}
                    busy={busy}
                    current={feedbackFor(audience)}
                    onSubmit={(body) =>
                      run(
                        () => publishFeedback(enrollmentId!, sequence, { audience, ...body }),
                        'Publicamos el comentario.',
                      )
                    }
                  />
                ))}
              </>
            )}
          </Section>
        )}

      {isTeacher && profile === 'LEP_0' && !isFinalProject && (
        <Section title="Registro de aprendizaje">
          <p style={mutedStyle}>
            Este curso es de Experiencia: la familia ve las clases a las que asistió, los temas y la
            próxima sesión. No hay evaluación, observación ni comentarios que registrar.
          </p>
        </Section>
      )}

      {isTeacher && !disputed && !isFinalProject && records?.admits.suggestedPractice && (
        <SuggestedPracticeForm
          busy={busy}
          sessionStarted={!!milestone.sessionStartedAt}
          current={practice}
          onSubmit={(body) =>
            run(
              () => recordSuggestedPractice(enrollmentId!, sequence, body),
              body.expectedVersion
                ? 'Guardamos la corrección de la práctica sugerida.'
                : 'Guardamos la práctica sugerida. La verán el alumno y su familia.',
            )
          }
        />
      )}

      {isTeacher && (
        <NotesForm
          busy={busy}
          current={milestone.teacherNotes}
          currentVersion={milestone.teacherNotesVersion ?? (milestone.teacherNotes ? 1 : undefined)}
          disabled={released}
          onSubmit={(body) =>
            run(
              () => submitNotes(enrollmentId!, sequence, body),
              body.expectedVersion
                ? 'Guardamos tus notas como una versión nueva. La anterior se conserva.'
                : 'Guardamos tus notas.',
            )
          }
        />
      )}

      {isTeacher && !locked && (!isFinalProject || closingOpen) && (
        <Section title={isFinalProject ? 'Cerrar el ciclo' : 'Liberar el pago'}>
          {complete ? (
            <>
              <p style={mutedStyle}>
                La evidencia está completa. Al confirmar,{' '}
                {formatMoney(milestone.amount, view?.currency)} se liberan de la garantía.
              </p>
              <button onClick={handleRelease} disabled={busy} style={btn(busy, '#2b8a3e')}>
                {isFinalProject
                  ? 'Cerrar el ciclo'
                  : `Completar y liberar ${formatMoney(milestone.amount, view?.currency)}`}
              </button>
            </>
          ) : (
            <p style={mutedStyle}>
              {isFinalProject
                ? 'El ciclo se cierra cuando todas las sesiones tengan su evidencia de entrega.'
                : 'Completa lo que falta arriba para poder liberar este pago. Basta la evidencia de entrega: grabación, tu asistencia y la duración mínima. Nunca depende de una evaluación.'}
            </p>
          )}
        </Section>
      )}

      {!isTeacher && familySeesRubric && (
        <ReadOnlyEvidence milestone={milestone} isFinalProject={isFinalProject} />
      )}

      {!isTeacher && !locked && !isFinalProject && (
        <Section title="El profesor no se presentó">
          <p style={mutedStyle}>
            Si el profesor no llegó, puedes reportarlo cuando pase el tiempo de espera de la clase.
            Revisamos el caso y te avisamos del resultado.
          </p>
          <button
            disabled={busy}
            onClick={() => handleNoShow('seller')}
            style={btn(busy, '#c92a2a')}
          >
            Reportar inasistencia del profesor
          </button>
        </Section>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ *
 * Sections
 * ------------------------------------------------------------------ */

const EvidenceChecklist: React.FC<{
  reasons: EvidenceFailureReason[];
  complete: boolean;
  released: boolean;
  isFinalProject: boolean;
}> = ({ reasons, complete, released, isFinalProject }) => {
  if (released) {
    return (
      <div style={alertStyle('success')}>
        Este {isFinalProject ? 'proyecto' : 'pago'} ya se liberó. La evidencia queda como registro.
      </div>
    );
  }
  if (complete) {
    return (
      <div style={alertStyle('success')}>
        Evidencia completa — este milestone ya puede liberarse.
      </div>
    );
  }
  return (
    <div style={alertStyle('warn')}>
      <strong>Falta evidencia para liberar este pago</strong>
      <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
        {reasons.map((r) => (
          <li key={r}>{EVIDENCE_REASON_LABELS[r] ?? r}</li>
        ))}
      </ul>
    </div>
  );
};

/**
 * T10-04: the two evidence facts, and the payment as a separate fact. Only
 * the delivery evidence decides the payment (MC01-R05); a learning-evidence
 * status that does not apply is said as "no aplica", never as missing.
 */
const GateFactLine: React.FC<{ fact: LearningGateFact }> = ({ fact }) => (
  <p style={{ ...mutedStyle, fontSize: 13 }} data-testid="gate-fact">
    Evidencia de entrega: <strong>{EVIDENCE_STATUS_LABEL[fact.deliveryStatus]}</strong> · Evidencia
    de aprendizaje: <strong>{EVIDENCE_STATUS_LABEL[fact.evidenceStatus]}</strong> (no condiciona el
    pago) · Pago: <strong>{fact.released ? 'liberado' : 'en garantía'}</strong>
  </p>
);

/**
 * What the platform recorded, read-only for everyone (UC-T09: attendance is a
 * provider-derived fact, never a control). Recording is shown as its state;
 * where it is stored is never sent to this screen.
 */
const SessionFacts: React.FC<{
  milestone: MilestoneEvidence;
  sessionState: string;
  isTeacher: boolean;
}> = ({ milestone, sessionState, isTeacher }) => (
  <Section title="Registro de la sesión">
    <dl style={factsStyle}>
      <Fact
        label="Estado"
        value={
          SESSION_STATE_LABEL[sessionState as keyof typeof SESSION_STATE_LABEL] ?? sessionState
        }
      />
      <Fact label="Inicio" value={fmtDateTime(milestone.sessionStartedAt)} />
      <Fact label="Fin" value={fmtDateTime(milestone.sessionEndedAt)} />
      <Fact
        label="Duración"
        value={
          typeof milestone.durationMinutes === 'number' ? `${milestone.durationMinutes} min` : '—'
        }
      />
      <Fact
        label={isTeacher ? 'Tu asistencia' : 'Asistencia del profesor'}
        value={fmtDateTime(milestone.teacherCheckedInAt)}
      />
      {milestone.type === 'session' && (
        <>
          <Fact
            label="Asistencia del alumno"
            value={
              milestone.attendance
                ? ATTENDANCE_LABELS[milestone.attendance]
                : milestone.sessionEndedAt
                ? '—'
                : 'Se registra al terminar la clase'
            }
          />
          <Fact label="Entró a la clase" value={fmtDateTime(milestone.learnerFirstJoinedAt)} />
          <Fact label="Tiempo conectado" value={formatPresence(milestone.learnerPresenceSeconds)} />
          <Fact
            label="Grabación"
            value={
              milestone.recordingStatus
                ? RECORDING_STATUS_LABELS[milestone.recordingStatus]
                : 'Sin grabación'
            }
          />
        </>
      )}
    </dl>
  </Section>
);

type RubricPayload = {
  scores: RubricScore[];
  overall: number;
  summary: string;
  asFinalRubric?: boolean;
};

/**
 * LEP_2/3: the first rubric is a submission; once it exists it is shown
 * read-only and changing it is a correction with a reason.
 */
const RubricBlock: React.FC<{
  busy: boolean;
  criteria: ReadonlyArray<{ key: string; label: string }>;
  existing?: Rubric;
  asFinalRubric: boolean;
  onSubmit: (payload: RubricPayload) => void;
  onCorrect: (
    payload: RubricPayload & { expectedVersion: number; reason: string; requestKey: string },
  ) => void;
}> = ({ busy, criteria, existing, asFinalRubric, onSubmit, onCorrect }) => {
  const [correcting, setCorrecting] = useState(false);
  const [requestKey, setRequestKey] = useState(newRequestKey);
  useEffect(() => {
    setCorrecting(false);
    setRequestKey(newRequestKey());
  }, [existing?.version]);

  const heading = asFinalRubric
    ? 'Evaluación del proyecto final (opcional)'
    : 'Evaluación de la sesión (opcional)';
  if (existing && !correcting) {
    return (
      <Section title={heading}>
        <RubricSummary rubric={existing} />
        <button
          type="button"
          disabled={busy}
          onClick={() => setCorrecting(true)}
          style={{ ...btn(busy, '#495057'), marginTop: 12 }}
        >
          Corregir evaluación
        </button>
      </Section>
    );
  }
  return (
    <RubricForm
      busy={busy}
      criteria={criteria}
      existing={existing}
      asFinalRubric={asFinalRubric}
      heading={existing ? `Corregir ${heading.toLowerCase()}` : heading}
      requireReason={!!existing}
      onCancel={existing ? () => setCorrecting(false) : undefined}
      onSubmit={(payload, reason) =>
        existing
          ? onCorrect({
              ...payload,
              expectedVersion: existing.version ?? 1,
              reason,
              requestKey,
            })
          : onSubmit(payload)
      }
    />
  );
};

const RubricSummary: React.FC<{ rubric: Rubric }> = ({ rubric }) => (
  <>
    <p style={{ margin: '0 0 8px' }}>
      Calificación general: <strong>{rubric.overall}</strong> / 5
      {rubric.version && rubric.version > 1 ? ` · versión ${rubric.version}` : ''}
    </p>
    <ul style={{ margin: '0 0 12px', paddingLeft: 20 }}>
      {rubric.scores.map((s) => (
        <li key={s.criterion}>
          {s.criterion}: {s.score} / 5
        </li>
      ))}
    </ul>
    <p style={{ margin: 0 }}>{rubric.summary}</p>
  </>
);

const RubricForm: React.FC<{
  busy: boolean;
  criteria: ReadonlyArray<{ key: string; label: string }>;
  existing?: { scores: RubricScore[]; overall: number; summary: string };
  asFinalRubric: boolean;
  heading: string;
  requireReason: boolean;
  onCancel?: () => void;
  onSubmit: (payload: RubricPayload, reason: string) => void;
}> = ({ busy, criteria, existing, asFinalRubric, heading, requireReason, onCancel, onSubmit }) => {
  const initialScores = useMemo(() => {
    const map: Record<string, number> = {};
    for (const c of criteria) {
      map[c.label] = existing?.scores.find((s) => s.criterion === c.label)?.score ?? 4;
    }
    return map;
  }, [criteria, existing]);

  const [scores, setScores] = useState<Record<string, number>>(initialScores);
  const [summary, setSummary] = useState(existing?.summary ?? '');
  const [reason, setReason] = useState('');

  useEffect(() => {
    setScores(initialScores);
    setSummary(existing?.summary ?? '');
  }, [initialScores, existing]);

  // The overall score is the average of the criteria rather than a separate
  // field, so a teacher cannot grade every criterion low and still pass. It is
  // the teacher's own scores averaged, not an automatic grade (MVP-04).
  const overall = useMemo(() => {
    const values = criteria.map((c) => scores[c.label] ?? 0);
    const avg = values.reduce((a, b) => a + b, 0) / (values.length || 1);
    return Math.round(avg * 10) / 10;
  }, [criteria, scores]);

  const summaryTooShort = summary.trim().length < 10;
  const reasonMissing = requireReason && reason.trim().length < 5;
  const disabled = busy || summaryTooShort || reasonMissing;

  return (
    <Section title={heading}>
      {criteria.map((c) => (
        <div key={c.key} style={{ marginBottom: 12 }}>
          <label htmlFor={`score-${c.key}`} style={labelStyle}>
            {c.label}: <strong>{scores[c.label] ?? 0}</strong> / 5
          </label>
          <input
            id={`score-${c.key}`}
            type="range"
            min={0}
            max={5}
            step={1}
            value={scores[c.label] ?? 0}
            onChange={(e) => setScores((p) => ({ ...p, [c.label]: Number(e.target.value) }))}
            style={{ width: '100%' }}
          />
        </div>
      ))}

      <p style={{ ...mutedStyle, marginTop: 0 }}>
        Calificación general: <strong>{overall}</strong> / 5 (promedio de los criterios)
      </p>

      <label htmlFor="rubric-summary" style={labelStyle}>
        Comentario para la familia
      </label>
      <textarea
        id="rubric-summary"
        value={summary}
        onChange={(e) => setSummary(e.target.value)}
        rows={4}
        placeholder="¿Qué trabajaron y cómo le fue al alumno? Mínimo 10 caracteres."
        style={textareaStyle}
      />
      {summaryTooShort && summary.length > 0 && (
        <p style={{ color: '#c92a2a', fontSize: 13, margin: '4px 0 0' }}>
          El comentario debe tener al menos 10 caracteres.
        </p>
      )}

      {requireReason && (
        <ReasonField
          id="rubric-correction-reason"
          value={reason}
          onChange={setReason}
          note="La evaluación anterior se conserva. Corregirla no cambia ningún pago ya hecho."
        />
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          disabled={disabled}
          onClick={() =>
            onSubmit(
              {
                scores: criteria.map((c) => ({ criterion: c.label, score: scores[c.label] ?? 0 })),
                overall,
                summary: summary.trim(),
                ...(asFinalRubric ? { asFinalRubric: true } : {}),
              },
              reason.trim(),
            )
          }
          style={btn(disabled)}
        >
          {requireReason ? 'Guardar corrección' : 'Guardar evaluación'}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} style={btn(false, '#868e96')}>
            Cancelar
          </button>
        )}
      </div>
    </Section>
  );
};

const ReasonField: React.FC<{
  id: string;
  value: string;
  onChange: (v: string) => void;
  note: string;
}> = ({ id, value, onChange, note }) => (
  <div style={{ marginTop: 12 }}>
    <label htmlFor={id} style={labelStyle}>
      Motivo de la corrección
    </label>
    <textarea
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={2}
      placeholder="¿Por qué la corriges? Mínimo 5 caracteres."
      style={textareaStyle}
    />
    <p style={{ ...mutedStyle, fontSize: 13, margin: '4px 0 0' }}>{note}</p>
  </div>
);

/** LEP_1: what the teacher saw, in four words that are not a grade. */
const ObservationForm: React.FC<{
  busy: boolean;
  sessionStarted: boolean;
  current?: LearningObservation;
  onSubmit: (body: {
    state: ObservationState;
    expectedVersion?: number;
    reason?: string;
    requestKey: string;
  }) => void;
}> = ({ busy, sessionStarted, current, onSubmit }) => {
  const [state, setState] = useState<ObservationState | ''>(current?.state ?? '');
  const [reason, setReason] = useState('');
  const [requestKey, setRequestKey] = useState(newRequestKey);
  useEffect(() => {
    setState(current?.state ?? '');
    setReason('');
    setRequestKey(newRequestKey());
  }, [current?.version, current?.state]);

  const correcting = !!current;
  const unchanged = correcting && state === current?.state;
  const disabled =
    busy || !state || unchanged || (correcting && reason.trim().length < 5) || !sessionStarted;

  return (
    <Section title="Observación de la sesión">
      <p style={mutedStyle}>{WORKING_VOCABULARY_NOTE}</p>
      {!sessionStarted && (
        <p style={mutedStyle}>Podrás registrar la observación cuando la sesión haya empezado.</p>
      )}
      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend style={labelStyle}>¿Cómo viste al alumno en esta sesión?</legend>
        {OBSERVATION_ORDER.map((s) => (
          <label key={s} style={{ display: 'block', marginBottom: 6, fontSize: 14 }}>
            <input
              type="radio"
              name="observation-state"
              value={s}
              checked={state === s}
              onChange={() => setState(s)}
              disabled={!sessionStarted}
            />{' '}
            <strong>{OBSERVATION_LABEL[s]}</strong> — {OBSERVATION_HELP[s]}
          </label>
        ))}
      </fieldset>
      {current && (
        <ReasonField
          id="observation-correction-reason"
          value={reason}
          onChange={setReason}
          note={`Ya registraste “${
            OBSERVATION_LABEL[current.state]
          }”. La versión anterior se conserva.`}
        />
      )}
      <button
        disabled={disabled}
        onClick={() =>
          state &&
          onSubmit({
            state,
            requestKey,
            ...(current ? { expectedVersion: current.version, reason: reason.trim() } : {}),
          })
        }
        style={{ ...btn(disabled), marginTop: 12 }}
      >
        {correcting ? 'Guardar corrección' : 'Guardar observación'}
      </button>
    </Section>
  );
};

/** One audience's feedback. Publishing is immediate; a change is a correction. */
const FeedbackForm: React.FC<{
  audience: FeedbackAudience;
  busy: boolean;
  current?: LearningFeedback;
  onSubmit: (body: {
    text: string;
    expectedVersion?: number;
    reason?: string;
    requestKey: string;
  }) => void;
}> = ({ audience, busy, current, onSubmit }) => {
  const [text, setText] = useState(current?.text ?? '');
  const [reason, setReason] = useState('');
  const [requestKey, setRequestKey] = useState(newRequestKey);
  useEffect(() => {
    setText(current?.text ?? '');
    setReason('');
    setRequestKey(newRequestKey());
  }, [current?.version, current?.text]);

  const id = `feedback-${audience.toLowerCase()}`;
  const correcting = !!current;
  const unchanged = correcting && text.trim() === current?.text;
  const disabled =
    busy || text.trim().length === 0 || unchanged || (correcting && reason.trim().length < 5);

  return (
    <div style={{ marginBottom: 16 }}>
      <label htmlFor={id} style={labelStyle}>
        {FEEDBACK_AUDIENCE_LABEL[audience]}
      </label>
      <textarea
        id={id}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        maxLength={2000}
        style={textareaStyle}
      />
      <p style={{ ...mutedStyle, fontSize: 13, margin: '4px 0 0' }}>
        {FEEDBACK_AUDIENCE_HELP[audience]}
      </p>
      {correcting && !unchanged && (
        <ReasonField
          id={`${id}-reason`}
          value={reason}
          onChange={setReason}
          note="El comentario anterior se conserva como historial."
        />
      )}
      <button
        disabled={disabled}
        onClick={() =>
          onSubmit({
            text: text.trim(),
            requestKey,
            ...(current ? { expectedVersion: current.version, reason: reason.trim() } : {}),
          })
        }
        style={{ ...btn(disabled, '#495057'), marginTop: 8 }}
      >
        {correcting ? 'Publicar corrección' : 'Publicar'}
      </button>
    </div>
  );
};

const DeliverableForm: React.FC<{
  busy: boolean;
  current?: string;
  onSubmit: (uri: string) => void;
}> = ({ busy, current, onSubmit }) => {
  const [uri, setUri] = useState(current ?? '');
  useEffect(() => setUri(current ?? ''), [current]);

  return (
    <Section title="Entregable del proyecto final (opcional)">
      <p style={mutedStyle}>
        Enlace al trabajo final del alumno, si lo hubo. No es obligatorio y no condiciona el pago.
      </p>
      <label htmlFor="deliverable-uri" style={labelStyle}>
        Enlace al entregable
      </label>
      <input
        id="deliverable-uri"
        value={uri}
        onChange={(e) => setUri(e.target.value)}
        placeholder="https://…"
        style={inputStyle}
      />
      <button
        disabled={busy || uri.trim().length === 0}
        onClick={() => onSubmit(uri.trim())}
        style={{ ...btn(busy || uri.trim().length === 0), marginTop: 12 }}
      >
        {current ? 'Actualizar entregable' : 'Guardar entregable'}
      </button>
    </Section>
  );
};

/**
 * MC01-R04 — what the class did to close the course. Optional in every
 * profile; a description, never a grade or an approval. Changing it after it
 * is saved is a correction with a reason, like every learning record.
 */
const ClosingActivityForm: React.FC<{
  busy: boolean;
  current?: ClosingActivity;
  onSubmit: (body: {
    summary: string;
    requestKey: string;
    expectedVersion?: number;
    reason?: string;
  }) => void;
}> = ({ busy, current, onSubmit }) => {
  const [summary, setSummary] = useState(current?.summary ?? '');
  const [reason, setReason] = useState('');
  const [requestKey, setRequestKey] = useState(newRequestKey);
  useEffect(() => {
    setSummary(current?.summary ?? '');
    setReason('');
    setRequestKey(newRequestKey());
  }, [current?.version, current?.summary]);

  const correcting = !!current;
  const blocked =
    busy ||
    summary.trim().length === 0 ||
    summary.trim() === (current?.summary ?? '') ||
    (correcting && reason.trim().length < 5);

  return (
    <Section title="Actividad de cierre (opcional)">
      <p style={mutedStyle}>
        Si cerraron el curso con una actividad en clase, descríbela en pocas palabras. No es una
        entrega ni se califica, y no hace falta para cerrar el ciclo.
      </p>
      <label htmlFor="closing-activity" style={labelStyle}>
        Qué hicieron en la actividad de cierre
      </label>
      <textarea
        id="closing-activity"
        value={summary}
        maxLength={1000}
        rows={3}
        onChange={(e) => setSummary(e.target.value)}
        style={textareaStyle}
      />
      {correcting && (
        <>
          <label htmlFor="closing-activity-reason" style={{ ...labelStyle, marginTop: 8 }}>
            Motivo del cambio
          </label>
          <input
            id="closing-activity-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={inputStyle}
          />
        </>
      )}
      <button
        type="button"
        disabled={blocked}
        onClick={() =>
          onSubmit({
            summary: summary.trim(),
            requestKey,
            ...(current ? { expectedVersion: current.version, reason: reason.trim() } : {}),
          })
        }
        style={{ ...btn(blocked), marginTop: 12 }}
      >
        {correcting ? 'Guardar corrección' : 'Guardar actividad de cierre'}
      </button>
    </Section>
  );
};

/**
 * TCH-14-FR-10 — the teacher's private notes. Each save is a new version;
 * changing saved notes asks why, and the earlier version is kept.
 */
const NotesForm: React.FC<{
  busy: boolean;
  current?: string;
  /** The version of `current`; undefined when nothing was saved yet. */
  currentVersion?: number;
  disabled: boolean;
  onSubmit: (body: {
    notes: string;
    requestKey: string;
    expectedVersion?: number;
    reason?: string;
  }) => void;
}> = ({ busy, current, currentVersion, disabled, onSubmit }) => {
  const [notes, setNotes] = useState(current ?? '');
  const [reason, setReason] = useState('');
  const [requestKey, setRequestKey] = useState(newRequestKey);
  useEffect(() => {
    setNotes(current ?? '');
    setReason('');
    setRequestKey(newRequestKey());
  }, [current, currentVersion]);

  const correcting = currentVersion !== undefined;
  const unchanged = notes === (current ?? '');
  const blocked = busy || disabled || unchanged || (correcting && reason.trim().length < 5);

  return (
    <Section title="Notas privadas">
      <p style={mutedStyle}>
        Sólo para ti y para el equipo de soporte si hiciera falta revisar la sesión. La familia
        nunca las ve.
      </p>
      <label htmlFor="teacher-notes" style={labelStyle}>
        Notas
      </label>
      <textarea
        id="teacher-notes"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={3}
        style={textareaStyle}
        disabled={disabled}
      />
      {correcting && !unchanged && (
        <ReasonField
          id="teacher-notes-reason"
          value={reason}
          onChange={setReason}
          note="La nota anterior se conserva como historial."
        />
      )}
      <button
        disabled={blocked}
        onClick={() =>
          onSubmit({
            notes,
            requestKey,
            ...(correcting ? { expectedVersion: currentVersion, reason: reason.trim() } : {}),
          })
        }
        style={{ ...btn(blocked, '#495057'), marginTop: 12 }}
      >
        {correcting ? 'Guardar nueva versión' : 'Guardar notas'}
      </button>
    </Section>
  );
};

/**
 * MC-02 — an optional suggestion to practise between classes. Not a task:
 * there is no due date, nothing to hand in and nothing graded, and the class
 * and the payment never wait on it. A change is a correction with a reason.
 */
const SuggestedPracticeForm: React.FC<{
  busy: boolean;
  sessionStarted: boolean;
  current?: SuggestedPractice;
  onSubmit: (body: {
    text: string;
    estimatedMinutes?: number;
    requestKey: string;
    expectedVersion?: number;
    reason?: string;
  }) => void;
}> = ({ busy, sessionStarted, current, onSubmit }) => {
  const [text, setText] = useState(current?.text ?? '');
  const [minutes, setMinutes] = useState(current?.estimatedMinutes?.toString() ?? '');
  const [reason, setReason] = useState('');
  const [requestKey, setRequestKey] = useState(newRequestKey);
  useEffect(() => {
    setText(current?.text ?? '');
    setMinutes(current?.estimatedMinutes?.toString() ?? '');
    setReason('');
    setRequestKey(newRequestKey());
  }, [current?.version, current?.text, current?.estimatedMinutes]);

  const parsedMinutes = minutes.trim() === '' ? undefined : Number(minutes);
  const minutesValid =
    parsedMinutes === undefined ||
    (Number.isInteger(parsedMinutes) && parsedMinutes >= 1 && parsedMinutes <= 240);
  const correcting = !!current;
  const unchanged =
    correcting && text.trim() === current?.text && parsedMinutes === current?.estimatedMinutes;
  const blocked =
    busy ||
    text.trim().length === 0 ||
    !minutesValid ||
    unchanged ||
    (correcting && reason.trim().length < 5);

  return (
    <Section title="Práctica sugerida (opcional)">
      {!sessionStarted ? (
        <p style={mutedStyle}>Podrás sugerir práctica cuando la sesión haya empezado.</p>
      ) : (
        <>
          <p style={mutedStyle}>
            Una idea para practicar entre clases, si el alumno quiere. No tiene fecha de entrega, no
            se entrega nada y no se califica. La ven el alumno y su familia.
          </p>
          <label htmlFor="suggested-practice" style={labelStyle}>
            Qué puede practicar
          </label>
          <textarea
            id="suggested-practice"
            value={text}
            maxLength={500}
            rows={2}
            onChange={(e) => setText(e.target.value)}
            style={textareaStyle}
          />
          <label htmlFor="suggested-practice-minutes" style={{ ...labelStyle, marginTop: 8 }}>
            Tiempo aproximado en minutos (opcional)
          </label>
          <input
            id="suggested-practice-minutes"
            type="number"
            min={1}
            max={240}
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
            style={{ ...inputStyle, maxWidth: 120 }}
          />
          {correcting && !unchanged && (
            <ReasonField
              id="suggested-practice-reason"
              value={reason}
              onChange={setReason}
              note="La sugerencia anterior se conserva como historial."
            />
          )}
          <button
            type="button"
            disabled={blocked}
            onClick={() =>
              onSubmit({
                text: text.trim(),
                ...(parsedMinutes !== undefined ? { estimatedMinutes: parsedMinutes } : {}),
                requestKey,
                ...(current ? { expectedVersion: current.version, reason: reason.trim() } : {}),
              })
            }
            style={{ ...btn(blocked), marginTop: 12 }}
          >
            {correcting ? 'Guardar corrección' : 'Guardar práctica sugerida'}
          </button>
        </>
      )}
    </Section>
  );
};

const ReadOnlyEvidence: React.FC<{ milestone: MilestoneEvidence; isFinalProject: boolean }> = ({
  milestone,
  isFinalProject,
}) => {
  const rubric = isFinalProject ? milestone.finalRubric : milestone.rubric;
  return (
    <Section title={isFinalProject ? 'Evaluación del proyecto final' : 'Evaluación de la sesión'}>
      {rubric ? (
        <RubricSummary rubric={rubric} />
      ) : (
        <p style={mutedStyle}>El profesor aún no ha registrado la evaluación.</p>
      )}
      {isFinalProject && milestone.deliverableUri && (
        <p style={{ marginTop: 12 }}>
          <a href={milestone.deliverableUri} target="_blank" rel="noopener noreferrer">
            Ver el entregable del proyecto final
          </a>
        </p>
      )}
    </Section>
  );
};

/* ------------------------------------------------------------------ *
 * Presentational bits
 * ------------------------------------------------------------------ */

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section style={sectionStyle}>
    <h2 style={{ fontSize: 16, margin: '0 0 12px' }}>{title}</h2>
    {children}
  </section>
);

const Fact: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <>
    <dt style={{ color: '#666', fontSize: 13 }}>{label}</dt>
    <dd style={{ margin: 0, fontSize: 14 }}>{value}</dd>
  </>
);

/* ------------------------------------------------------------------ *
 * Styles
 * ------------------------------------------------------------------ */

const backLinkStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#0366d6',
  cursor: 'pointer',
  padding: 0,
  marginBottom: 16,
};

const sectionStyle: React.CSSProperties = {
  border: '1px solid #d1d5da',
  borderRadius: 8,
  padding: 16,
  marginBottom: 16,
};

const factsStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'max-content 1fr',
  gap: '6px 16px',
  margin: 0,
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  color: '#495057',
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 8,
  border: '1px solid #d1d5da',
  borderRadius: 6,
  fontSize: 14,
  boxSizing: 'border-box',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  fontFamily: 'inherit',
  resize: 'vertical',
};

const mutedStyle: React.CSSProperties = { color: '#666', fontSize: 14, marginTop: 0 };

const btn = (disabled: boolean, color = '#1971c2'): React.CSSProperties => ({
  padding: '8px 14px',
  border: 'none',
  borderRadius: 6,
  background: disabled ? '#adb5bd' : color,
  color: '#fff',
  fontSize: 14,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

const alertStyle = (kind: 'error' | 'success' | 'warn'): React.CSSProperties => {
  const palette = {
    error: { background: '#ffe3e3', border: '#ff8787', color: '#c92a2a' },
    success: { background: '#d3f9d8', border: '#51cf66', color: '#2b8a3e' },
    warn: { background: '#fff3bf', border: '#ffd43b', color: '#8a6d00' },
  }[kind];
  return {
    background: palette.background,
    border: `1px solid ${palette.border}`,
    color: palette.color,
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    fontSize: 14,
  };
};

export default SessionEvidence;
